// 付箋色の選好を localStorage に保存する。
// 色は任意の hex 文字列（"#RRGGBB"）として扱い、組み込みパレットと利用者が追加した
// カスタム色（最大 STICKY_CUSTOM_COLOR_LIMIT 件）の組み合わせから選ぶ。

import { isValidHexColor, normalizeHexColor } from "./pen-preference";

const COLOR_KEY = "refinement-canvas:sticky:color";
const CUSTOM_COLORS_KEY = "refinement-canvas:sticky:custom-colors";

// 付箋色は hex 文字列で扱う（明示のための別名）。
export type StickyColor = string;

// 既存の 4 色プリセット相当を hex のまま定義する。視認性の都合で淡い色に揃えている。
export const STICKY_PRESET_COLORS: readonly StickyColor[] = [
  "#fff5b3",
  "#ffd6e7",
  "#dbeafe",
  "#dcfce7",
];

const DEFAULT_COLOR: StickyColor = STICKY_PRESET_COLORS[0] as StickyColor;

/** ローカル保持できる付箋のカスタム色の上限。 */
export const STICKY_CUSTOM_COLOR_LIMIT = 12;

// v0.2.31 で StickyColor を enum から hex 文字列へ変更した。旧バージョンの localStorage に
// 保存された "yellow" / "pink" / "blue" / "green" を、新形式の hex 値へ読み替える辞書。
// 既存利用者の選択色が初回起動時に既定色（黄）へ戻らないようにする。
const LEGACY_STICKY_COLOR_HEX: Record<string, StickyColor> = {
  yellow: "#fff5b3",
  pink: "#ffd6e7",
  blue: "#dbeafe",
  green: "#dcfce7",
};

export function loadStickyColor(): StickyColor {
  try {
    const value = window.localStorage.getItem(COLOR_KEY);
    if (isValidHexColor(value)) {
      return normalizeHexColor(value);
    }
    // 旧 enum 形式が残っている場合は対応する hex 値へ透過的に読み替える。
    // saveStickyColor が次回呼ばれたタイミングで hex 形式へ書き換わるのに任せる。
    const legacy = value ? LEGACY_STICKY_COLOR_HEX[value] : undefined;
    if (legacy) return legacy;
    return DEFAULT_COLOR;
  } catch {
    return DEFAULT_COLOR;
  }
}

export function saveStickyColor(color: StickyColor): void {
  if (!isValidHexColor(color)) return;
  try {
    window.localStorage.setItem(COLOR_KEY, normalizeHexColor(color));
  } catch {
    // localStorage が使えない環境では保存のみ諦める。
  }
}

/** 追加保存された付箋のカスタム色一覧を読み込む。壊れた値は読み飛ばす。 */
export function loadStickyCustomColors(): readonly StickyColor[] {
  try {
    const raw = window.localStorage.getItem(CUSTOM_COLORS_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    const result: StickyColor[] = [];
    for (const value of parsed) {
      if (isValidHexColor(value)) {
        const normalized = normalizeHexColor(value);
        if (!result.includes(normalized)) result.push(normalized);
      }
      if (result.length >= STICKY_CUSTOM_COLOR_LIMIT) break;
    }
    return result;
  } catch {
    return [];
  }
}

/**
 * 付箋のカスタム色を 1 件追加する。
 * - 組み込みプリセットに含まれる色は重複としてカスタム側へは追加しない。
 * - 既にカスタム色に存在する場合は追加せず、現在のリストを返す。
 * - 上限 {@link STICKY_CUSTOM_COLOR_LIMIT} に達している場合は古い方を 1 件捨てる（FIFO）。
 *
 * 返り値は更新後のカスタム色一覧。
 */
export function addStickyCustomColor(color: StickyColor): readonly StickyColor[] {
  if (!isValidHexColor(color)) return loadStickyCustomColors();
  const normalized = normalizeHexColor(color);
  if (STICKY_PRESET_COLORS.includes(normalized)) return loadStickyCustomColors();
  const current = loadStickyCustomColors();
  if (current.includes(normalized)) return current;
  const next = [...current, normalized];
  while (next.length > STICKY_CUSTOM_COLOR_LIMIT) next.shift();
  try {
    window.localStorage.setItem(CUSTOM_COLORS_KEY, JSON.stringify(next));
  } catch {
    // ignore
  }
  return next;
}
